/**
 * Shared transcript export utilities.
 *
 * Extracted from InlineTranscriptPanel so both the transcript panel's own
 * copy/download buttons AND the QuickActionLauncher palette entries can
 * share ONE source of truth for the markdown format + copy + download
 * behaviour. No duplication, no drift between call sites.
 */

/**
 * Build a clean markdown export of the transcript.
 *   • Session timestamp header (ISO minute precision).
 *   • Per-turn `**Qoryn:** …` / `**You:** …` heading + normalized text.
 *   • Where corrections exist, an italicized `_(raw: "…")_` annotation
 *     is appended so provenance is preserved without cluttering the
 *     primary reading experience.
 *   • In-progress turns are annotated `[in progress]` so paste-mid-turn
 *     is honest rather than misleading.
 * Returns { markdown, filename }. filename = qoryn-session-YYYY-MM-DD-HHmm.md
 */
export function buildTranscriptMarkdown(turns, sessionStartedAt) {
    const startedIso = sessionStartedAt
        ? new Date(sessionStartedAt).toISOString()
        : new Date().toISOString();
    const dt = new Date(startedIso);
    const pad = (n) => String(n).padStart(2, "0");
    const filename = `qoryn-session-${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}-${pad(dt.getHours())}${pad(dt.getMinutes())}.md`;

    const header = [
        `# Qoryn Voice — Session Transcript`,
        ``,
        `- Session started: ${startedIso}`,
        `- Turns: ${turns.length}`,
        `- Exported: ${new Date().toISOString()}`,
        ``,
        `---`,
        ``,
    ].join("\n");

    const body = turns
        .map((t) => {
            const speaker = t.role === "user" ? "You" : "Qoryn";
            const text = (t.normalized ?? "").trim();
            const raw = (t.raw ?? "").trim();
            const inProgress = t.status === "in_progress" ? " _[in progress]_" : "";
            const rawAnnot =
                t.corrections?.length && raw && raw !== text
                    ? `\n  _(raw: "${raw.replace(/"/g, '\\"')}")_`
                    : "";
            return `**${speaker}:**${inProgress} ${text}${rawAnnot}`;
        })
        .join("\n\n");

    return { markdown: header + body + "\n", filename };
}

/** Copy transcript markdown to clipboard. Returns true on success. */
export async function copyTranscriptMarkdown(turns, sessionStartedAt) {
    if (!turns || turns.length === 0) return false;
    const { markdown } = buildTranscriptMarkdown(turns, sessionStartedAt);
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(markdown);
        } else {
            const ta = document.createElement("textarea");
            ta.value = markdown;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
        }
        return true;
    } catch (e) {
        console.warn("[transcript] copy failed", e);
        return false;
    }
}

/** Trigger a browser download of the transcript markdown. Returns true on success. */
export function downloadTranscriptMarkdown(turns, sessionStartedAt) {
    if (!turns || turns.length === 0) return false;
    const { markdown, filename } = buildTranscriptMarkdown(turns, sessionStartedAt);
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
}
